<!-- Карта маршрута рейса по готовым route legs -->
<template>
  <div class="trip-route-map" :class="{ 'trip-route-map--compact': compact }">
    <div v-if="routeLegs.length > 0" ref="mapContainer" class="trip-route-map__canvas"></div>
    <div v-else class="trip-route-map__empty">
      <span>Маршрут не построен</span>
      <button
        v-if="compact"
        class="trip-route-map__refresh"
        type="button"
        @click.stop="$emit('refresh')"
      >
        Пересчитать
      </button>
    </div>
  </div>
</template>

<script>
import Feature from "ol/Feature";
import Polyline from "ol/format/Polyline";
import Point from "ol/geom/Point";
import Map from "ol/Map";
import View from "ol/View";
import { defaults as defaultControls } from "ol/control";
import TileLayer from "ol/layer/Tile";
import { Vector as VectorLayer } from "ol/layer";
import { Vector as VectorSource } from "ol/source";
import XYZ from "ol/source/XYZ";
import {
  Circle as CircleStyle,
  Fill,
  Stroke,
  Style,
  Text,
} from "ol/style";
import "ol/ol.css";

import { getLegColor } from "@/utils/mapUtils";

export default {
  name: "TripRouteMap",

  props: {
    routeLegs: {
      type: Array,
      default: () => [],
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },

  emits: ["refresh"],

  data() {
    return {
      map: null,
      vectorSource: null,
      resizeObserver: null,
    };
  },

  watch: {
    routeLegs: {
      deep: true,
      handler() {
        this.renderRouteOnMap();
      },
    },
  },

  mounted() {
    this.$nextTick(() => this.initMap());
  },

  beforeUnmount() {
    if (this.resizeObserver) this.resizeObserver.disconnect();
    if (!this.map) return;

    this.map.setTarget(null);
    this.map = null;
  },

  methods: {
    initMap() {
      const container = this.$refs.mapContainer;
      if (!container || this.map || !this.routeLegs.length) return;

      const tileLayer = new TileLayer({
        source: new XYZ({
          url: "https://tile.openstreetmap.org/{z}/{x}/{y}.png",
          attributions: "&copy; OpenStreetMap",
          maxZoom: 19,
        }),
      });

      this.vectorSource = new VectorSource();
      const vectorLayer = new VectorLayer({
        source: this.vectorSource,
      });

      this.map = new Map({
        target: container,
        layers: [tileLayer, vectorLayer],
        view: new View({
          center: [0, 0],
          zoom: 2,
        }),
        controls: defaultControls({
          attribution: false,
          zoom: !this.compact,
          rotate: false,
        }),
      });

      this.resizeObserver = new ResizeObserver(() => {
        if (this.map) this.map.updateSize();
      });
      this.resizeObserver.observe(container);

      this.renderRouteOnMap();
    },

    renderRouteOnMap() {
      if (!this.routeLegs.length) {
        this.vectorSource?.clear();
        return;
      }
      if (!this.map) {
        this.$nextTick(() => this.initMap());
        return;
      }
      if (!this.vectorSource) return;

      this.vectorSource.clear();

      const format = new Polyline({ factor: 1e5 });

      this.routeLegs.forEach((leg, index) => {
        const color = getLegColor(index);

        if (leg.polyline) {
          const geometry = format.readGeometry(leg.polyline, {
            dataProjection: "EPSG:4326",
            featureProjection: "EPSG:3857",
          });

          const lineFeature = new Feature({ geometry });
          lineFeature.setStyle(
            new Style({
              stroke: new Stroke({
                color,
                width: this.compact ? 3 : 5,
              }),
            })
          );
          this.vectorSource.addFeature(lineFeature);
        }

        if (leg.trip_point_from) {
          this.addMarker(
            leg.trip_point_from,
            color,
            leg.trip_point_from.sequence_number
          );
        }

        if (
          index === this.routeLegs.length - 1 &&
          leg.trip_point_to
        ) {
          this.addMarker(
            leg.trip_point_to,
            color,
            leg.trip_point_to.sequence_number
          );
        }
      });

      setTimeout(() => this.fitRoute(), 0);
    },

    addMarker(pointData, color, label) {
      const coords = [Number(pointData.lon), Number(pointData.lat)];
      const geometry = new Point(coords).transform(
        "EPSG:4326",
        "EPSG:3857"
      );

      const marker = new Feature({ geometry });
      const radius = this.compact ? 8 : 14;

      marker.setStyle(
        new Style({
          image: new CircleStyle({
            radius,
            fill: new Fill({ color }),
            stroke: new Stroke({ color: "#fff", width: 2 }),
          }),
          text: new Text({
            text: String(label),
            fill: new Fill({ color: "#fff" }),
            font: this.compact ? "bold 10px sans-serif" : "bold 14px sans-serif",
            offsetY: 1,
          }),
        })
      );

      this.vectorSource.addFeature(marker);
    },

    fitRoute() {
      if (!this.vectorSource?.getFeatures().length) return;

      this.map.getView().fit(this.vectorSource.getExtent(), {
        padding: this.compact ? [18, 18, 18, 18] : [50, 50, 50, 50],
        duration: this.compact ? 0 : 1000,
      });
    },
  },
};
</script>

<style scoped>
.trip-route-map {
  width: 100%;
  height: 500px;
  overflow: hidden;
  background-color: #f0f0f0;
  border: 1px solid #dfe3e8;
  border-radius: 8px;
}

.trip-route-map--compact {
  height: 100%;
  min-height: 112px;
}

.trip-route-map__canvas {
  width: 100%;
  height: 100%;
}

.trip-route-map__empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 7px;
  width: 100%;
  height: 100%;
  padding: 10px;
  color: #94a3b8;
  font-size: 12px;
  line-height: 1.25;
  text-align: center;
}

.trip-route-map__refresh {
  padding: 4px 8px;
  color: var(--color-primary);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  background: #fff;
  border: 1px solid #cfd8dc;
  border-radius: 6px;
  cursor: pointer;
}

.trip-route-map__refresh:hover {
  background: #f8fafc;
  border-color: var(--color-primary);
}

.trip-route-map--compact .trip-route-map__canvas {
  pointer-events: none;
}
</style>
